import { differenceInSeconds } from 'date-fns';
import {
    AgentWorkState,
    ChannelGroupType,
    ChannelProviderType,
    ChannelType,
    QueueMediaType,
    RoutingModeType,
} from '@dxp/core/types';
import { floorToSecond, toPercentage } from '@dxp/core/helpers';
import { ITelXLKpiQueueThreshold } from '../rbac/queue-kpi';
import { IQueueAttributeRequirement } from '../rbac/workflow';
import { IValueWithMetric } from '../metrics/metric';
import { IMenuItemsData } from '../menu/menu-items';
import { NBU_ID } from '../business-unit/business-unit';
import {
    IQueueActivity,
    QueueActivity,
    QueueActivityStatus,
} from './queue-activity';

export interface IQueue {
    id: string;
    name: string;
    businessUnit?: string;
    hasActivity: boolean;
    isConfigured: boolean;
    report?: IQueueWithChannelsReport;
    information?: IQueueInformation;
    kpiTelXLThreshold: ITelXLKpiQueueThreshold;
    channelProviders: ChannelProviderType[];
    mediaType: QueueMediaType;
    channelGroups: ChannelGroupType[];
    startOfToday: Date | null;
    attributeRequirements?: IQueueAttributeRequirement[];

    visibleByChannelFilter(filter: ChannelGroupType | undefined): boolean;
    activity(index?: string): IQueueActivity[];
    statistics(index?: string): IQueueStatistics;
}

export interface IQueueInformation {
    id: string;
    name: string;
    workflowId: string;
    mediaGroup: string;
    queueSegments: IQueueSegment[];
    agentSegments: IAgentSegment[];
    todaysQueueSegments: IQueueSegment[];
    todaysAgentSegments: IAgentSegment[];
    segments: IQueueSegment[];
    businessUnitId: string;
    staffedLoggedInAgentCount: number;
    staffedAvailableAgentCount: number;
}

export interface IQueueSegment {
    queueId: string;
    queuedAt: Date;
    queueName: string;
    deQueuedAt?: Date;
    startedAt: Date;
    endedAt?: Date;
    position?: number;
    customer: IQueueCustomer;
    channelId: number;
    channelType: ChannelType;
    channelGroup: ChannelGroupType;
    routingMode: RoutingModeType;
    conversationId: string;
    businessUnitId: string;
    canAssign: boolean;
}

export interface IAgentSegment {
    customer: IQueueCustomer;
    createdAt: Date;
    closedAt?: Date;
    startedAt: Date;
    endedAt?: Date;
    completedAt?: Date;
    status?: AgentWorkState;
    queueId: string;
    conversationId: string;
    agents: IQueueAgent[];
}

export interface IQueueCustomer {
    id: string;
    firstName: string;
    middleName: string;
    lastName: string;
    phoneNumber: string;
    emailAddress: string;
}

export interface IQueueAgent {
    id: string;
    name: string;
}

export interface IQueueReport {
    id: string;
    name: string;
    channelId: string;
    channelType: ChannelType;
    channelGroup: ChannelGroupType;
    sla: number;
    abandonThreshold: number;
    startOfToday: Date;
    answeredWithinSLARatio: number;
    handledCount: number;
    handledWithinSLACount: number;
    abandonedCount: number;
    abandonedWithinSLACount: number;
    callbackRequestedCount: number;
    callbackAcceptedCount: number;
    longestWaitTimeInSeconds: number;
    avgWaitingTimeInSeconds: number;
    queueTimedOutCount: number;
    conversationCountForAvgWaitingTime: number;
    businessUnitId: string;
}

export interface IQueueWithChannelsReport extends IQueueReport {
    queueChannelReports: IQueueReport[];
}

export interface IQueueStatistics {
    answeredWithinSLAPercentage: IValueWithMetric;
    abandoned: number;
    timedOut: number;
    waiting: IValueWithMetric;
    connected: number;
    success: number;
    complete: number;
    avgWaitingTimeInSeconds: IValueWithMetric;
    currentLongestWaitingTimeInSeconds: IValueWithMetric;
    longestWaitingTimeInSeconds: IValueWithMetric;
    outOfSla: number;
    sla: number;
    avgHandlingTimeInSeconds: IValueWithMetric;
}

export interface IQueuePerformanceVm {
    menuItemsData: IMenuItemsData[];
    queueVm: IQueueVm;
}

export interface IQueueVm {
    high: number;
    kpiTelXLThreshold: ITelXLKpiQueueThreshold;
    mediaType: QueueMediaType;
    enabled: boolean;
    statistics: IQueueStatistics;
}

export class Queue implements IQueue {
    readonly id: string;
    readonly #unableToAssign: string[];
    readonly #information?: IQueueInformation;
    readonly #report?: IQueueWithChannelsReport;
    readonly #activity: QueueActivity[];
    readonly #kpiTelXLThreshold: ITelXLKpiQueueThreshold;
    readonly #channelProviders?: ChannelProviderType[];
    readonly #attributeRequirements?: IQueueAttributeRequirement[];

    constructor(
        id: string,
        kpiTelXLThreshold: ITelXLKpiQueueThreshold,
        unableToAssign: string[],
        channelProviders?: ChannelProviderType[],
        information?: IQueueInformation,
        report?: IQueueWithChannelsReport,
        attributeRequirements?: IQueueAttributeRequirement[],
    ) {
        this.id = id;
        this.#unableToAssign = unableToAssign;
        this.#channelProviders = channelProviders;
        this.#information = information;
        this.#report = report;
        this.#kpiTelXLThreshold = kpiTelXLThreshold;
        this.#attributeRequirements = attributeRequirements;

        const agentSegments = [
            ...(this.#information?.agentSegments ?? []),
            ...(this.#information?.todaysAgentSegments ?? []),
        ];
        const queueSegments = [
            ...(this.#information?.queueSegments ?? []),
            ...(this.#information?.todaysQueueSegments ?? []),
        ];

        this.#activity = queueSegments.map(
            q =>
                new QueueActivity(
                    this.startOfToday !== null &&
                        new Date(q.queuedAt).getTime() >=
                            this.startOfToday.getTime(),
                    this.#unableToAssign.includes(q.conversationId),
                    this,
                    q,
                    agentSegments.find(
                        a => a.conversationId === q.conversationId,
                    ),
                ),
        );
    }

    get name(): string {
        return this.#information?.name ?? '';
    }

    get businessUnit(): string | undefined {
        return this.#information?.businessUnitId ?? NBU_ID;
    }

    get isConfigured(): boolean {
        return this.mediaType !== 'Unknown';
    }

    get hasActivity(): boolean {
        return this.#activity.some(a => a.includeInReport);
    }

    get report(): IQueueWithChannelsReport | undefined {
        return this.#report;
    }

    get information(): IQueueInformation | undefined {
        return this.#information;
    }

    get attributeRequirements(): IQueueAttributeRequirement[] {
        return this.#attributeRequirements ?? [];
    }

    get kpiTelXLThreshold(): ITelXLKpiQueueThreshold {
        return this.#kpiTelXLThreshold;
    }

    get channelProviders(): ChannelProviderType[] {
        return this.#channelProviders ?? [];
    }

    get mediaType(): QueueMediaType {
        switch (this.#information?.mediaGroup) {
            case 'voice':
                return 'Voice';
            case 'email':
                return 'Email';
            case 'chat': {
                if (this.channelProviders.length === 0) return 'Unknown';

                if (
                    this.channelProviders.includes('Webchat') &&
                    this.channelProviders.length === 1
                )
                    return 'Webchat';
                if (
                    this.channelProviders.includes('Webchat') &&
                    this.channelProviders.length > 1
                )
                    return 'Mixed';
                return 'Socials';
            }
            default:
                return 'Unknown';
        }
    }

    get channelGroups(): ChannelGroupType[] {
        switch (this.mediaType) {
            case 'Voice':
                return ['voice'];
            case 'Email':
                return ['email'];
            case 'Webchat':
                return ['webchat'];
            case 'Socials':
                return ['socials'];
            case 'Mixed':
                return ['webchat', 'socials'];
            default:
                return ['email', 'voice', 'webchat', 'socials'];
        }
    }

    get startOfToday(): Date | null {
        if (this.#report?.startOfToday)
            return new Date(this.#report.startOfToday);

        if (
            this.#information?.queueSegments &&
            this.#information.queueSegments.length > 0
        )
            return new Date(
                this.#information.queueSegments.reduce(
                    (earliest, segment) =>
                        segment.queuedAt < earliest
                            ? segment.queuedAt
                            : earliest,
                    this.#information.queueSegments[0].queuedAt,
                ),
            );

        if (
            this.#information?.agentSegments &&
            this.#information.agentSegments.length > 0
        ) {
            const earliestAgent = this.#information.agentSegments.reduce(
                (earliest, segment) =>
                    segment.createdAt < earliest.createdAt ? segment : earliest,
                this.#information.agentSegments[0],
            );

            const queuedAt = this.#information.todaysQueueSegments.find(
                tqs => tqs.conversationId === earliestAgent.conversationId,
            )?.queuedAt;

            return queuedAt ? new Date(queuedAt) : null;
        }

        return null;
    }

    visibleByChannelFilter(filter: ChannelGroupType | undefined): boolean {
        if (!filter) return true;

        return this.channelGroups.includes(filter);
    }

    activity(index?: string): IQueueActivity[] {
        if (index === undefined) {
            return this.#activity;
        }

        return this.#activity.filter(a => a.channelGroup === index);
    }

    statistics(index?: ChannelGroupType): IQueueStatistics {
        if (!this.#report || !this.#information) {
            // Manually set waiting and connected if report isn't available on 1st queued item.
            let minimumQueuedAt =
                this.#information && this.#information?.queueSegments.length > 0
                    ? this.information?.queueSegments.reduce((min, current) => {
                          return new Date(current.queuedAt).getTime() <
                              min.getTime()
                              ? new Date(current.queuedAt)
                              : min;
                      }, new Date())
                    : new Date();

            minimumQueuedAt = floorToSecond(minimumQueuedAt ?? new Date());

            return queueStatisticDefault(
                this.#activity?.filter(a =>
                    a.channelGroup === index
                        ? index
                        : a.channelGroup &&
                          a.status === QueueActivityStatus.Waiting &&
                          a.includeInReport,
                ).length,
                minimumQueuedAt,
                this.#activity?.filter(a =>
                    a.channelGroup === index
                        ? index
                        : a.channelGroup &&
                          a.status === QueueActivityStatus.Connented &&
                          a.includeInReport,
                ).length,
                this.#kpiTelXLThreshold,
            );
        }

        if (index === undefined) {
            return this.#calculatePerformance(
                this.#report,
                this.#information.queueSegments,
                this.#information.agentSegments,
                this.activity().filter(
                    a =>
                        a.status === QueueActivityStatus.Completed &&
                        a.includeInReport,
                ),
            );
        }

        const report = this.#report.queueChannelReports.find(
            qcr => qcr.channelGroup === index,
        );

        if (report) {
            const conversationsMap = new Set(
                this.#information.todaysQueueSegments
                    .filter(q => q.channelGroup === index)
                    .map(q => q.conversationId),
            );

            return this.#calculatePerformance(
                report,
                this.#information.queueSegments.filter(
                    qs => qs.channelGroup === index,
                ),
                this.#information.agentSegments.filter(as =>
                    conversationsMap.has(as.conversationId),
                ),
                this.activity(index).filter(
                    a =>
                        a.status === QueueActivityStatus.Completed &&
                        a.includeInReport,
                ),
            );
        }

        return queueStatisticDefault(0, new Date(), 0, this.#kpiTelXLThreshold);
    }

    #calculatePerformance(
        report: IQueueReport,
        queueSegments: IQueueSegment[],
        agentSegments: IAgentSegment[],
        activity: IQueueActivity[],
    ): IQueueStatistics {
        let minimumQueuedAt =
            queueSegments.length > 0
                ? queueSegments.reduce((min, current) => {
                      return new Date(current.queuedAt).getTime() <
                          min.getTime()
                          ? new Date(current.queuedAt)
                          : min;
                  }, new Date())
                : new Date();

        minimumQueuedAt = floorToSecond(minimumQueuedAt);

        const averageHandlingTimeInSeconds =
            activity.length > 0
                ? activity.reduce(
                      (acc, act) => acc + act.handlingTimeDuration,
                      0,
                  ) / activity.length
                : 0;

        return {
            answeredWithinSLAPercentage: {
                value: toPercentage(Math.max(report.answeredWithinSLARatio, 0)),
                metric: {
                    percent: toPercentage(
                        Math.max(report.answeredWithinSLARatio, 0),
                    ),
                    states: this.#kpiTelXLThreshold.slaThreshold.enabled
                        ? [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold.slaThreshold.low,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold.slaThreshold.high,
                                  ),
                                  state: 'success',
                              },
                          ]
                        : [],
                },
            },
            abandoned: report.abandonedCount,
            timedOut: report.queueTimedOutCount,
            waiting: {
                value: queueSegments.length,
                metric: this.#kpiTelXLThreshold.maxConversationsWaiting.enabled
                    ? {
                          percent: toPercentage(
                              queueSegments.length /
                                  this.#kpiTelXLThreshold
                                      .maxConversationsWaiting.high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold
                                          .maxConversationsWaiting.low /
                                          this.#kpiTelXLThreshold
                                              .maxConversationsWaiting.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            connected: agentSegments.length,
            success: Math.max(report.handledWithinSLACount, 0),
            complete: activity.length,
            avgWaitingTimeInSeconds: {
                value: report.avgWaitingTimeInSeconds,
                metric: this.#kpiTelXLThreshold.averageWaitTimeSeconds.enabled
                    ? {
                          percent: toPercentage(
                              report.avgWaitingTimeInSeconds /
                                  this.#kpiTelXLThreshold.averageWaitTimeSeconds
                                      .high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold
                                          .averageWaitTimeSeconds.low /
                                          this.#kpiTelXLThreshold
                                              .averageWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            currentLongestWaitingTimeInSeconds: {
                value: differenceInSeconds(new Date(), minimumQueuedAt),
                metric: this.#kpiTelXLThreshold.maxLongestWaitTimeSeconds
                    .enabled
                    ? {
                          percent: this.#kpiTelXLThreshold
                              .maxLongestWaitTimeSeconds.high
                              ? toPercentage(
                                    differenceInSeconds(
                                        new Date(),
                                        minimumQueuedAt,
                                    ) /
                                        this.#kpiTelXLThreshold
                                            .maxLongestWaitTimeSeconds.high,
                                )
                              : 0,
                          states: [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.low /
                                          this.#kpiTelXLThreshold
                                              .maxLongestWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            longestWaitingTimeInSeconds: {
                value: report.longestWaitTimeInSeconds,
                metric: this.#kpiTelXLThreshold.maxLongestWaitTimeSeconds
                    .enabled
                    ? {
                          percent: toPercentage(
                              report.longestWaitTimeInSeconds /
                                  this.#kpiTelXLThreshold
                                      .maxLongestWaitTimeSeconds.high,
                          ),
                          states: [
                              {
                                  value: toPercentage(
                                      this.#kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.low /
                                          this.#kpiTelXLThreshold
                                              .maxLongestWaitTimeSeconds.high,
                                  ),
                                  state: 'warning',
                              },
                              {
                                  value: 100,
                                  state: 'danger',
                              },
                          ],
                      }
                    : { percent: 0, states: [] },
            },
            outOfSla:
                report.sla > 0
                    ? Math.max(
                          activity.length - report.handledWithinSLACount,
                          0,
                      )
                    : 0,
            sla: report.sla,
            avgHandlingTimeInSeconds: {
                value: averageHandlingTimeInSeconds,
                metric:
                    this.#kpiTelXLThreshold.averageHandlingTimeSeconds &&
                    this.#kpiTelXLThreshold.averageHandlingTimeSeconds.enabled
                        ? {
                              percent: toPercentage(
                                  averageHandlingTimeInSeconds /
                                      this.#kpiTelXLThreshold
                                          .averageHandlingTimeSeconds.target,
                              ),
                              states: [
                                  {
                                      value: toPercentage(
                                          this.#kpiTelXLThreshold
                                              .averageHandlingTimeSeconds.low /
                                              this.#kpiTelXLThreshold
                                                  .averageHandlingTimeSeconds
                                                  .target,
                                      ),
                                      state: 'warning',
                                  },
                                  {
                                      value: toPercentage(
                                          this.#kpiTelXLThreshold
                                              .averageHandlingTimeSeconds.high /
                                              this.#kpiTelXLThreshold
                                                  .averageHandlingTimeSeconds
                                                  .target,
                                      ),
                                      state: 'danger',
                                  },
                              ],
                          }
                        : { percent: 0, states: [] },
            },
        };
    }
}

const queueStatisticDefault = (
    waiting: number,
    queuedAt: Date,
    connected: number,
    kpiTelXLThreshold: ITelXLKpiQueueThreshold,
): IQueueStatistics => {
    return {
        answeredWithinSLAPercentage: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        waiting: { value: waiting, metric: { percent: 0, states: [] } },
        connected: connected,
        success: 0,
        complete: 0,
        timedOut: 0,
        abandoned: 0,
        outOfSla: 0,
        sla: -1,
        longestWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        avgWaitingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
        currentLongestWaitingTimeInSeconds: {
            value: differenceInSeconds(new Date(), queuedAt),
            metric: kpiTelXLThreshold.maxLongestWaitTimeSeconds.enabled
                ? {
                      percent: kpiTelXLThreshold.maxLongestWaitTimeSeconds.high
                          ? toPercentage(
                                differenceInSeconds(new Date(), queuedAt) /
                                    kpiTelXLThreshold.maxLongestWaitTimeSeconds
                                        .high,
                            )
                          : 0,
                      states: [
                          {
                              value: toPercentage(
                                  kpiTelXLThreshold.maxLongestWaitTimeSeconds
                                      .low /
                                      kpiTelXLThreshold
                                          .maxLongestWaitTimeSeconds.high,
                              ),
                              state: 'warning',
                          },
                          {
                              value: 100,
                              state: 'danger',
                          },
                      ],
                  }
                : { percent: 0, states: [] },
        },
        avgHandlingTimeInSeconds: {
            value: 0,
            metric: { percent: 0, states: [] },
        },
    };
};
